import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Card } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import Actions from 'actions';
import Selectors from 'selectors';
import { ProfileContext } from 'utils/context';
import * as Yup from 'yup';
import notify from 'utils/notification';
import {
  fromSelectValuesToIdObjects,
  toSelectValues,
  USER_CLASSIFICATION_TYPES,
} from '../Onboarding/options';
import Personal from './Personal';
import ProfessionalProfile from './ProfessionalProfile';
import Playlist from './Playlist';
import Services from './Services';
import Work from './Work';
import InterestsAndEvents from './InterestsAndEvents';

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required('Required'),
  last_name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  member_types: Yup.array().max(3, 'Maximum 3 member types allowed').required('Required'),
  industry_areas: Yup.array().max(3, 'Choose up to 3 industry areas'),
  professional_goals: Yup.array().max(3, 'Choose up to 3 goals'),
  target_markets: Yup.array().max(5, 'Choose up to 5 target markets'),
  professional_roles: Yup.array().max(3, 'Choose up to 3 creative roles'),
  interest_topics: Yup.array().max(5, 'Choose up to 5 genres'),
  event_types: Yup.array().max(3, 'Choose up to 3 event types'),
});

const ViewProfile = ({ initialValues, readOnly }) => {
  const dispatch = useDispatch();
  const countries = useSelector(Selectors.countries);
  const userClassifications = useSelector(Selectors.userClassifications);
  const isLoadingClassifications = useSelector(
    Selectors.isLoadingUserClassifications
  );
  const isLoadingTargetMarkets = useSelector(
    Selectors.createLoadingSelector([Actions.FETCH_COUNTRIES_REQUEST])
  );
  const classificationOptions = useMemo(() => {
    const apiOptions = Object.entries(USER_CLASSIFICATION_TYPES).reduce(
      (options, [fieldName, classificationType]) => ({
        ...options,
        [fieldName]: toSelectValues(
          userClassifications[classificationType]?.data || []
        ),
      }),
      {}
    );

    return {
      ...apiOptions,
      target_markets: toSelectValues(countries),
    };
  }, [countries, userClassifications]);

  useEffect(() => {
    if (readOnly) return;

    dispatch(Actions.fetchCountries());
    Object.values(USER_CLASSIFICATION_TYPES).forEach((type) => {
      dispatch(Actions.fetchUserClassifications({ type }));
    });
  }, [dispatch, readOnly]);

  const handleOnSubmit = (formValues) => {
    const {
      member_types: memberTypes,
      languages,
      rep_artists: repArtists,
      rep_events: repEvents,
      rep_venues: repVenues,
      users_services: userServices,
      users_experiences: userExperiences,
      industry_areas: industryAreas,
      professional_goals: professionalGoals,
      target_markets: targetMarkets,
      professional_skills: professionalSkills,
      professional_roles: professionalRoles,
      interest_topics: interestTopics,
      music_genres: musicGenres,
      event_types: eventTypes,
      event_formats: eventFormats,
      event_attend_locations: eventAttendLocations,
    } = formValues;

    const parsedFormValue = {
      ...formValues,
      member_types: memberTypes,
      languages: languages.map((item) => item.value),
      rep_artists: repArtists.split(', '),
      rep_events: repEvents.split(', '),
      rep_venues: repVenues.split(', '),
      industry_areas: fromSelectValuesToIdObjects(industryAreas),
      professional_goals: fromSelectValuesToIdObjects(professionalGoals),
      target_markets: fromSelectValuesToIdObjects(targetMarkets),
      professional_skills: fromSelectValuesToIdObjects(professionalSkills),
      professional_roles: fromSelectValuesToIdObjects(professionalRoles),
      interest_topics: fromSelectValuesToIdObjects(interestTopics),
      music_genres: fromSelectValuesToIdObjects(musicGenres),
      event_types: fromSelectValuesToIdObjects(eventTypes),
      event_formats: fromSelectValuesToIdObjects(eventFormats),
      event_attend_locations: fromSelectValuesToIdObjects(
        eventAttendLocations
      ),
      users_services_attributes: userServices,
      users_experiences_attributes: userExperiences,
    };

    dispatch(
      Actions.updateUser(parsedFormValue, {
        success: () => notify('Profile updated'),
        failure: (e) => notify(e, null, 'danger'),
      })
    );
  };

  return (
    <div className="container mx-auto px-6 pt-4 mb-24">
      <ProfileContext.Provider value={{ isView: true, readOnly }}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          enableReinitialize
          validationSchema={validationSchema}
        >
          <Form>
            <Card className="mt-4">
              <Personal isView />
            </Card>
            <Card className="p-8 mt-8">
              <ProfessionalProfile
                classificationOptions={classificationOptions}
                isLoadingClassifications={isLoadingClassifications}
                isLoadingTargetMarkets={isLoadingTargetMarkets}
              />
            </Card>
            <Card className="p-8 mt-8">
              <Work />
            </Card>
            <Card className="p-8 mt-8">
              <Services />
            </Card>
            <Card className="p-8 mt-8">
              <InterestsAndEvents
                classificationOptions={classificationOptions}
                isLoadingClassifications={isLoadingClassifications}
              />
            </Card>
            <Card className="p-8 mt-8">
              <Playlist />
            </Card>
          </Form>
        </Formik>
      </ProfileContext.Provider>
    </div>
  );
};

ViewProfile.propTypes = {
  initialValues: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
};

ViewProfile.defaultProps = {
  readOnly: false,
};

export default ViewProfile;
